import React, { useMemo } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   ACTIVITY STRUCTURING INSIGHTS PANEL (C2)
   ----------------------------------------------------------------------------
   LOGIQUE
   - panneau de contrôle du package actif
   - lecture courte, opérationnelle, non narrative
   - cohérence avec la nouvelle logique C1 issue du sandbox B1/B2
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  border: theme?.colors?.borders?.default || "#d6dee4",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",
};

const MOCK_PACKAGE = {
  id: "p2",
  name: "Bloc traitement – ligne 1",
  type: "Sous-système",
  logic: "Par système",
  status: "À consolider",
  items: [
    {
      id: "i1",
      label: "Pompes",
      family: "Équipements",
      discipline: "Process",
      zone: "Ligne 1",
      intensity: "Critique",
    },
    {
      id: "i2",
      label: "Skids process",
      family: "Équipements",
      discipline: "Process",
      zone: "Ligne 1",
      intensity: "Lourd",
    },
    {
      id: "i7",
      label: "Montage équipements",
      family: "Main d’œuvre",
      discipline: "Process",
      zone: "Ligne 1",
      intensity: "Critique",
    },
  ],
};

/* ============================================================================
   HELPERS
============================================================================ */

const getSignalColor = (tone) => {
  switch (tone) {
    case "green":
      return T.greenText;
    case "yellow":
      return T.yellowText;
    case "orange":
      return T.orangeText;
    case "red":
      return T.redText;
    default:
      return T.greyText;
  }
};

const countBy = (items = [], getter) => {
  return items.reduce((acc, item) => {
    const key = getter(item);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
};

const getDominantKey = (map) => {
  const entries = Object.entries(map || {});
  if (!entries.length) return "-";
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
};

/* ============================================================================
   UI BLOCKS
============================================================================ */

const PanelBlock = ({ title, children }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      paddingBottom: 2,
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.84rem",
        lineHeight: 1.12,
        color: theme?.colors?.text?.caneva || "#c24c2c",
        fontWeight: 600,
      }}
    >
      {title}
    </div>

    {children}
  </div>
);

const KeyValueRow = ({ label, value, valueColor = T.text }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr auto",
      alignItems: "center",
      gap: 12,
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.74rem",
        lineHeight: 1.2,
        color: T.soft,
        minWidth: 0,
      }}
    >
      {label}
    </div>

    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.76rem",
        lineHeight: 1.2,
        color: valueColor,
        fontWeight: 600,
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </div>
  </div>
);

const SignalRow = ({ text, tone = "grey" }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      padding: "4px 0",
    }}
  >
    <div
      style={{
        width: 5,
        height: 5,
        borderRadius: 999,
        background: getSignalColor(tone),
        marginTop: 6,
        flexShrink: 0,
      }}
    />

    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.72rem",
        lineHeight: 1.35,
        color: T.soft,
      }}
    >
      {text}
    </div>
  </div>
);

const EmptyState = () => (
  <div
    style={{
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 22,
      boxSizing: "border-box",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minHeight: 12,
      }}
    >
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "1.02rem",
          lineHeight: 1.1,
          color: T.text,
          fontWeight: 600,
        }}
      >
        Structuring Overview
      </div>
    </div>

    <PanelBlock title="Selected package">
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.78rem",
          lineHeight: 1.35,
          color: T.verySoft,
        }}
      >
        Sélectionner un package dans C1 pour afficher sa composition, ses interfaces
        et sa granularité.
      </div>
    </PanelBlock>
  </div>
);

/* ============================================================================
   METRICS
============================================================================ */

const computeMetrics = (data) => {
  const items = data?.items || [];

  const disciplineMap = countBy(items, (item) => item.discipline || "Non défini");
  const familyMap = countBy(items, (item) => item.family || "Non défini");
  const zoneMap = countBy(items, (item) => item.zone || "Non défini");

  const disciplines = Object.keys(disciplineMap);
  const families = Object.keys(familyMap);
  const zones = Object.keys(zoneMap);

  const equipmentCount = familyMap["Équipements"] || 0;
  const materialCount = familyMap["Matériaux"] || 0;
  const laborCount = familyMap["Main d’œuvre"] || 0;
  const criticalCount = items.filter((item) => item.intensity === "Critique").length;

  const dominantDiscipline = getDominantKey(disciplineMap);
  const dominantZone = getDominantKey(zoneMap);

  let coherence = "Homogène";
  let coherenceTone = "green";

  if (disciplines.length >= 2 || zones.length >= 2) {
    coherence = "Mixte";
    coherenceTone = "yellow";
  }

  if (disciplines.length >= 3 || (zones.length >= 2 && criticalCount >= 1)) {
    coherence = "À scinder";
    coherenceTone = "orange";
  }

  let granularity = "Pilotable";
  let granularityTone = "green";

  if (items.length <= 2) {
    granularity = "Trop fin";
    granularityTone = "yellow";
  }

  if (items.length >= 6 || disciplines.length >= 3) {
    granularity = "Trop large";
    granularityTone = "orange";
  }

  const interfaceSignals = [];

  if (disciplines.includes("Process") && disciplines.includes("Électricité")) {
    interfaceSignals.push({
      tone: "orange",
      text: "Interface Process ↔ Électricité à consolider.",
    });
  }

  if (disciplines.includes("Génie civil") && disciplines.includes("Process")) {
    interfaceSignals.push({
      tone: "yellow",
      text: "Interface Génie civil ↔ Process à vérifier.",
    });
  }

  if (disciplines.includes("Réseaux") && zones.length >= 1) {
    interfaceSignals.push({
      tone: "grey",
      text: "Coordination linéaires / zones à confirmer.",
    });
  }

  if (!interfaceSignals.length) {
    interfaceSignals.push({
      tone: "green",
      text: "Peu d’interfaces croisées à ce stade.",
    });
  }

  const actionSignals = [];

  if (granularity === "Trop large") {
    actionSignals.push({
      tone: "orange",
      text: "Scinder le package en sous-unités plus pilotables.",
    });
  }

  if (granularity === "Trop fin") {
    actionSignals.push({
      tone: "yellow",
      text: "Fusionner avec un package voisin ou élargir le périmètre.",
    });
  }

  if (coherence === "À scinder") {
    actionSignals.push({
      tone: "orange",
      text: "Réduire les interfaces structurantes dans le même package.",
    });
  }

  if (criticalCount > 0) {
    actionSignals.push({
      tone: "yellow",
      text: "Isoler ou suivre les éléments critiques.",
    });
  }

  if (!actionSignals.length) {
    actionSignals.push({
      tone: "green",
      text: "Le package peut servir de base directe à la suite.",
    });
  }

  return {
    itemCount: items.length,
    disciplineCount: disciplines.length,
    zoneCount: zones.length,
    dominantDiscipline,
    dominantZone,
    equipmentCount,
    materialCount,
    laborCount,
    criticalCount,
    coherence,
    coherenceTone,
    granularity,
    granularityTone,
    interfaceSignals: interfaceSignals.slice(0, 4),
    actionSignals: actionSignals.slice(0, 4),
  };
};

/* ============================================================================
   MAIN COMPONENT
============================================================================ */

const ActivityStructuringInsightsPanel = ({
  selectedPackage = null,
  useMock = true,
}) => {
  const data = selectedPackage || (useMock ? MOCK_PACKAGE : null);

  const metrics = useMemo(() => {
    if (!data) return null;
    return computeMetrics(data);
  }, [data]);

  if (!data || !metrics) return <EmptyState />;

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 22,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          minHeight: 12,
        }}
      >
        <div
          style={{
            fontFamily: theme?.fonts?.body || "inherit",
            fontSize: "1.02rem",
            lineHeight: 1.1,
            color: T.text,
            fontWeight: 600,
          }}
        >
          Structuring Overview
        </div>
      </div>

      <PanelBlock title="Package snapshot">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <KeyValueRow label="Package" value={data.name} valueColor={T.blueText} />
          <KeyValueRow label="Type" value={data.type || "-"} />
          <KeyValueRow label="Logic" value={data.logic || "-"} />
          <KeyValueRow label="Status" value={data.status || "-"} />
        </div>
      </PanelBlock>

      <PanelBlock title="Composition">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <KeyValueRow label="Elements" value={`${metrics.itemCount}`} />
          <KeyValueRow label="Disciplines" value={`${metrics.disciplineCount}`} />
          <KeyValueRow label="Zones covered" value={`${metrics.zoneCount}`} />
          <KeyValueRow label="Dominant discipline" value={metrics.dominantDiscipline} valueColor={T.blueText} />
          <KeyValueRow label="Main zone" value={metrics.dominantZone} />
          <KeyValueRow label="Matériaux" value={`${metrics.materialCount}`} valueColor={T.blueText} />
          <KeyValueRow label="Labor" value={`${metrics.laborCount}`} valueColor={T.orangeText} />
          <KeyValueRow label="Équipements" value={`${metrics.equipmentCount}`} valueColor={T.greenText} />
        </div>
      </PanelBlock>

      <PanelBlock title="Interfaces">
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {metrics.interfaceSignals.map((signal, index) => (
            <SignalRow key={`interface-${index}`} text={signal.text} tone={signal.tone} />
          ))}
        </div>
      </PanelBlock>

      <PanelBlock title="Granularity & coherence">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <KeyValueRow
            label="Granularity"
            value={metrics.granularity}
            valueColor={getSignalColor(metrics.granularityTone)}
          />
          <KeyValueRow
            label="Coherence"
            value={metrics.coherence}
            valueColor={getSignalColor(metrics.coherenceTone)}
          />
          <KeyValueRow
            label="Critical elements"
            value={`${metrics.criticalCount}`}
            valueColor={metrics.criticalCount > 0 ? T.orangeText : T.greenText}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Recommended actions">
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {metrics.actionSignals.map((signal, index) => (
            <SignalRow key={`action-${index}`} text={signal.text} tone={signal.tone} />
          ))}
        </div>
      </PanelBlock>

      <PanelBlock title="Downstream impact">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <KeyValueRow label="Specification" value="Base de spécifications groupées" />
          <KeyValueRow label="Budget" value="Prépare les quantités structurées" />
          <KeyValueRow label="Planning" value="Clarifie les séquences futures" />
        </div>
      </PanelBlock>
    </div>
  );
};

export default ActivityStructuringInsightsPanel;